import {CheckCircleFillIcon, CheckCircleIcon, StopIcon} from '@primer/octicons-react'
import {Flash, IconButton} from '@primer/react'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {ConversationHeader} from './ConversationHeader'
import {useState} from 'react'

import styles from './ThreadHeader.module.css'
import {useResolveThreadMutation} from '../../mutations/use-resolve-thread-mutation'
import {useUnresolveThreadMutation} from '../../mutations/use-unresolve-thread-mutation'
import type {fileLevelCommentKey, LineKey} from '@github-ui/diff-lines/types'
import type {ThreadPreviewComp} from '../../page-data/payloads/thread-previews'
import {isLoggedIn} from '@github-ui/client-env'

type ThreadHeaderProps = {
  firstCommentId?: number | null
  isCollapsed: boolean
  onNavigateToDiffComment: () => void
  onToggleCollapsed: () => void
  threadPreview: ThreadPreviewComp
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  isResolved: boolean
  isOutdated: boolean
  lineKey: LineKey | typeof fileLevelCommentKey
}

export function ThreadHeader({
  firstCommentId,
  isCollapsed,
  onToggleCollapsed,
  onNavigateToDiffComment,
  threadPreview,
  headingLevel,
  isResolved,
  isOutdated,
  lineKey,
}: ThreadHeaderProps) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const previewInfo = threadPreview.previewInfo
  const {line} = previewInfo
  const canResolveConversation = isLoggedIn()

  const {mutate: resolveThreadMutation} = useResolveThreadMutation(previewInfo.path, lineKey)
  const handleResolveThread = () => {
    if (isResolved) return

    resolveThreadMutation(
      {threadId: previewInfo.threadId},
      {
        onError: () => setErrorMessage('Failed to resolve thread'),
        onSuccess: () => {
          if (!isCollapsed) onToggleCollapsed()
        },
      },
    )
  }

  const {mutate: unresolveThreadMutation} = useUnresolveThreadMutation(previewInfo.path, lineKey)
  const handleUnresolveThread = async () => {
    if (!threadPreview) return
    if (!isResolved) return

    unresolveThreadMutation(
      {threadId: threadPreview.previewInfo.threadId},
      {
        onError: () => setErrorMessage('Failed to unresolve thread'),
        onSuccess: () => {
          if (isCollapsed) onToggleCollapsed()
        },
      },
    )

    sendPullRequestAnalyticsEvent('comments.unresolve_thread', 'RESOLVE_CONVERSATION_BUTTON')
  }

  return (
    <>
      <ConversationHeader
        firstCommentId={firstCommentId}
        isCollapsed={isCollapsed}
        isOutdated={isOutdated}
        isResolved={isResolved}
        line={line}
        path={previewInfo.path}
        rightSideContent={
          canResolveConversation ? (
            <IconButton
              aria-label={isResolved ? 'Unresolve conversation' : 'Resolve conversation'}
              tooltipDirection="sw"
              icon={isResolved ? CheckCircleFillIcon : CheckCircleIcon}
              // need to be specific in order to override default IconButton color
              data-thread-resolved={isResolved}
              className={styles.ThreadHeaderIconButton}
              variant="invisible"
              onClick={isResolved ? handleUnresolveThread : handleResolveThread}
            />
          ) : null
        }
        onNavigateToDiffComment={onNavigateToDiffComment}
        onToggleCollapsed={onToggleCollapsed}
        threadId={previewInfo.threadId}
        headingLevel={headingLevel}
      />
      {errorMessage && (
        <Flash variant="danger" className="m-2">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </>
  )
}
